import React, { useState, useEffect, useRef } from "react";
import {
  Save,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  Image as ImageIcon,
  Upload,
  Loader2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useAuth } from "../../contexts/AuthContext";
import { useSiteConfig } from "../../contexts/SiteConfigContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { key: "general", label: "General / Images" },
  { key: "home", label: "Home Page" },
  { key: "about", label: "About Page" },
  { key: "services", label: "Services" },
  { key: "audiences", label: "Who Needs This" },
  { key: "howItWorks", label: "How It Works" },
  { key: "faq", label: "FAQ" },
  { key: "footer", label: "Footer" },
];

export default function SiteConfigPage() {
  const { authAxios } = useAuth();
  const { refreshConfig } = useSiteConfig();
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState("general");

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await authAxios.get("/site-config");
      setConfig(res.data);
    } catch (err) {
      console.error("Failed to fetch config", err);
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (section) => {
    setSaving(true);
    setMessage(null);
    try {
      await authAxios.put(`/site-config/${section}`, config[section]);
      // Refresh the global site config so public pages see updates immediately
      await refreshConfig();
      setMessage({ type: "success", text: `"${section}" saved successfully! Changes are now live.` });
      setTimeout(() => setMessage(null), 4000);
    } catch (err) {
      setMessage({ type: "error", text: "Failed to save. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, path, value) => {
    setConfig((prev) => {
      const newConfig = { ...prev };
      const sectionData = JSON.parse(JSON.stringify(newConfig[section] || {}));
      const keys = path.split(".");
      let target = sectionData;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = isNaN(keys[i]) ? keys[i] : parseInt(keys[i]);
        target = target[k];
      }
      const lastKey = isNaN(keys[keys.length - 1]) ? keys[keys.length - 1] : parseInt(keys[keys.length - 1]);
      target[lastKey] = value;
      newConfig[section] = sectionData;
      return newConfig;
    });
  };

  const addArrayItem = (section, path, template) => {
    setConfig((prev) => {
      const newConfig = { ...prev };
      const sectionData = JSON.parse(JSON.stringify(newConfig[section] || {}));
      if (path) {
        const keys = path.split(".");
        let target = sectionData;
        for (let i = 0; i < keys.length; i++) {
          const k = keys[i];
          const key = isNaN(k) ? k : parseInt(k);
          // Initialize array if it doesn't exist
          if (target[key] === undefined) {
            target[key] = [];
          }
          if (i === keys.length - 1) {
            // Last key - push to this array
            if (Array.isArray(target[key])) {
              target[key].push(template);
            }
          } else {
            target = target[key];
          }
        }
      } else {
        if (Array.isArray(sectionData)) {
          sectionData.push(template);
          newConfig[section] = sectionData;
          return newConfig;
        }
      }
      newConfig[section] = sectionData;
      return newConfig;
    });
  };

  const removeArrayItem = (section, path, index) => {
    setConfig((prev) => {
      const newConfig = { ...prev };
      const sectionData = JSON.parse(JSON.stringify(newConfig[section]));
      if (path) {
        const keys = path.split(".");
        let target = sectionData;
        for (const k of keys) {
          target = target[isNaN(k) ? k : parseInt(k)];
        }
        target.splice(index, 1);
      } else {
        if (Array.isArray(sectionData)) {
          sectionData.splice(index, 1);
          newConfig[section] = sectionData;
          return newConfig;
        }
      }
      newConfig[section] = sectionData;
      return newConfig;
    });
  };

  if (loading) {
    return <div className="p-8 text-gray-500">Loading site configuration...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Site Configurator</h1>
          <p className="text-gray-500 text-sm">Manage all website content, images, and feature cards</p>
        </div>
        {message && (
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
            message.type === "success" ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"
          }`}>
            {message.type === "success" ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            {message.text}
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#111118] border border-white/[0.04] p-1 mb-8 flex-wrap h-auto gap-1">
          {SECTIONS.map((s) => (
            <TabsTrigger
              key={s.key}
              value={s.key}
              className="data-[state=active]:bg-[#DC2626]/10 data-[state=active]:text-[#DC2626] text-gray-400 text-xs px-4 py-2"
            >
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* GENERAL / IMAGES */}
        <TabsContent value="general">
          <SectionWrapper onSave={() => saveSection("general")} saving={saving}>
            <FieldGroup title="Site Information">
              <FieldRow>
                <Field label="Site Name" value={config.general?.name} onChange={(v) => updateField("general", "name", v)} />
                <Field label="Tagline" value={config.general?.tagline} onChange={(v) => updateField("general", "tagline", v)} />
              </FieldRow>
              <FieldRow>
                <Field label="Phone" value={config.general?.phone} onChange={(v) => updateField("general", "phone", v)} />
                <Field label="Email" value={config.general?.email} onChange={(v) => updateField("general", "email", v)} />
              </FieldRow>
              <FieldRow>
                <Field label="Location" value={config.general?.location} onChange={(v) => updateField("general", "location", v)} />
                <Field label="Hours" value={config.general?.hours} onChange={(v) => updateField("general", "hours", v)} />
              </FieldRow>
              <FieldRow>
                <Field 
                  label="Logo Size (e.g., 5.5rem, 80px, 6rem)" 
                  value={config.general?.logoSize || "5.5rem"} 
                  onChange={(v) => updateField("general", "logoSize", v)} 
                />
              </FieldRow>
            </FieldGroup>

            <FieldGroup title="Images">
              {(() => {
                const imgs = config.general?.images || {};
                const defaultKeys = ["hero", "logo", "desk", "detective", "evidenceBoard", "corporate", "magnifyingGlass", "office"];
                const allKeys = [...new Set([...Object.keys(imgs), ...defaultKeys])];
                return allKeys.map((key) => (
                  <ImageFieldWithUpload
                    key={key}
                    label={key.replace(/([A-Z])/g, ' $1').trim()}
                    value={imgs[key] || ""}
                    onChange={(newUrl) => {
                      if (!config.general?.images) {
                        updateField("general", "images", { ...imgs, [key]: newUrl });
                      } else {
                        updateField("general", `images.${key}`, newUrl);
                      }
                    }}
                    authAxios={authAxios}
                  />
                ));
              })()}
              <Button variant="outline" size="sm" onClick={() => {
                const newKey = prompt("Enter image key name (e.g., 'banner'):");
                if (newKey) {
                  const imgs = config.general?.images || {};
                  if (!config.general?.images) {
                    updateField("general", "images", { ...imgs, [newKey]: "" });
                  } else {
                    updateField("general", `images.${newKey}`, "");
                  }
                }
              }} className="text-gray-400 border-dashed border-white/10 text-xs mt-2">
                <Plus className="h-3 w-3 mr-1" /> Add Image
              </Button>
            </FieldGroup>
          </SectionWrapper>
        </TabsContent>

        {/* HOME PAGE */}
        <TabsContent value="home">
          <SectionWrapper onSave={() => saveSection("home")} saving={saving}>
            <FieldGroup title="Hero Section">
              <Field label="Badge Text" value={config.home?.heroBadge} onChange={(v) => updateField("home", "heroBadge", v)} />
              <Field label="Hero Title" value={config.home?.heroTitle} onChange={(v) => updateField("home", "heroTitle", v)} />
              <FieldTextarea label="Hero Subtitle" value={config.home?.heroSubtitle} onChange={(v) => updateField("home", "heroSubtitle", v)} />
            </FieldGroup>

            <FieldGroup title="Stats">
              {config.home?.stats?.map((stat, i) => (
                <div key={i} className="flex gap-3 mb-3 items-end">
                  <div className="flex-1">
                    <Label className="text-gray-500 text-xs">Value</Label>
                    <Input value={stat.value} onChange={(e) => updateField("home", `stats.${i}.value`, e.target.value)} className="bg-[#0A0A0F] border-white/[0.06] text-white text-xs h-9" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-gray-500 text-xs">Label</Label>
                    <Input value={stat.label} onChange={(e) => updateField("home", `stats.${i}.label`, e.target.value)} className="bg-[#0A0A0F] border-white/[0.06] text-white text-xs h-9" />
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeArrayItem("home", "stats", i)} className="text-red-400 hover:bg-red-500/10 h-9 px-2">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addArrayItem("home", "stats", { value: "", label: "" })} className="text-gray-400 border-dashed border-white/10 text-xs mt-2">
                <Plus className="h-3 w-3 mr-1" /> Add Stat
              </Button>
            </FieldGroup>

            <FieldGroup title="Why Choose Us">
              {config.home?.whyChooseUs?.map((item, i) => (
                <CollapsibleCard key={i} title={item.title || `Item ${i + 1}`} onDelete={() => removeArrayItem("home", "whyChooseUs", i)}>
                  <FieldRow>
                    <Field label="Icon (lucide)" value={item.icon} onChange={(v) => updateField("home", `whyChooseUs.${i}.icon`, v)} />
                    <Field label="Title" value={item.title} onChange={(v) => updateField("home", `whyChooseUs.${i}.title`, v)} />
                  </FieldRow>
                  <FieldTextarea label="Description" value={item.description} onChange={(v) => updateField("home", `whyChooseUs.${i}.description`, v)} />
                </CollapsibleCard>
              ))}
              <Button variant="outline" size="sm" onClick={() => addArrayItem("home", "whyChooseUs", { icon: "Shield", title: "", description: "" })} className="text-gray-400 border-dashed border-white/10 text-xs mt-2">
                <Plus className="h-3 w-3 mr-1" /> Add Item
              </Button>
            </FieldGroup>
          </SectionWrapper>
        </TabsContent>

        {/* ABOUT */}
        <TabsContent value="about">
          <SectionWrapper onSave={() => saveSection("about")} saving={saving}>
            <FieldGroup title="About Content">
              <FieldTextarea label="Hero Subtitle" value={config.about?.heroSubtitle} onChange={(v) => updateField("about", "heroSubtitle", v)} />
              <FieldTextarea label="Mission" value={config.about?.mission} onChange={(v) => updateField("about", "mission", v)} />
            </FieldGroup>
            <FieldGroup title="Story Paragraphs">
              {config.about?.storyParagraphs?.map((p, i) => (
                <div key={i} className="flex gap-2 mb-3">
                  <Textarea value={p} onChange={(e) => updateField("about", `storyParagraphs.${i}`, e.target.value)} rows={3} className="bg-[#0A0A0F] border-white/[0.06] text-white text-xs flex-1 resize-none" />
                  <Button variant="ghost" size="sm" onClick={() => removeArrayItem("about", "storyParagraphs", i)} className="text-red-400 hover:bg-red-500/10 h-9 px-2 self-start">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addArrayItem("about", "storyParagraphs", "")} className="text-gray-400 border-dashed border-white/10 text-xs mt-2">
                <Plus className="h-3 w-3 mr-1" /> Add Paragraph
              </Button>
            </FieldGroup>
            <FieldGroup title="Values">
              {config.about?.values?.map((v, i) => (
                <CollapsibleCard key={i} title={v.title || `Value ${i + 1}`} onDelete={() => removeArrayItem("about", "values", i)}>
                  <FieldRow>
                    <Field label="Icon" value={v.icon} onChange={(val) => updateField("about", `values.${i}.icon`, val)} />
                    <Field label="Title" value={v.title} onChange={(val) => updateField("about", `values.${i}.title`, val)} />
                  </FieldRow>
                  <FieldTextarea label="Description" value={v.description} onChange={(val) => updateField("about", `values.${i}.description`, val)} />
                </CollapsibleCard>
              ))}
              <Button variant="outline" size="sm" onClick={() => addArrayItem("about", "values", { icon: "Shield", title: "", description: "" })} className="text-gray-400 border-dashed border-white/10 text-xs mt-2">
                <Plus className="h-3 w-3 mr-1" /> Add Value
              </Button>
            </FieldGroup>
            <FieldGroup title="Credentials">
              {config.about?.credentials?.map((c, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input value={c} onChange={(e) => updateField("about", `credentials.${i}`, e.target.value)} className="bg-[#0A0A0F] border-white/[0.06] text-white text-xs h-9 flex-1" />
                  <Button variant="ghost" size="sm" onClick={() => removeArrayItem("about", "credentials", i)} className="text-red-400 hover:bg-red-500/10 h-9 px-2">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addArrayItem("about", "credentials", "")} className="text-gray-400 border-dashed border-white/10 text-xs mt-2">
                <Plus className="h-3 w-3 mr-1" /> Add Credential
              </Button>
            </FieldGroup>
          </SectionWrapper>
        </TabsContent>

        {/* SERVICES */}
        <TabsContent value="services">
          <SectionWrapper onSave={() => saveSection("services")} saving={saving}>
            {config.services?.map((svc, i) => (
              <CollapsibleCard key={i} title={svc.title || `Service ${i + 1}`} onDelete={() => removeArrayItem("services", null, i)}>
                <FieldRow>
                  <Field label="Icon (lucide)" value={svc.icon} onChange={(v) => updateField("services", `${i}.icon`, v)} />
                  <Field label="Title" value={svc.title} onChange={(v) => updateField("services", `${i}.title`, v)} />
                </FieldRow>
                <FieldTextarea label="Description" value={svc.shortDescription} onChange={(v) => updateField("services", `${i}.shortDescription`, v)} />
                <Label className="text-gray-500 text-xs block mt-3 mb-2">Features</Label>
                {svc.features?.map((f, fi) => (
                  <div key={fi} className="flex gap-2 mb-2">
                    <Input value={f} onChange={(e) => updateField("services", `${i}.features.${fi}`, e.target.value)} className="bg-[#0A0A0F] border-white/[0.06] text-white text-xs h-8 flex-1" />
                    <Button variant="ghost" size="sm" onClick={() => removeArrayItem("services", `${i}.features`, fi)} className="text-red-400 hover:bg-red-500/10 h-8 px-2">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addArrayItem("services", `${i}.features`, "")} className="text-gray-400 border-dashed border-white/10 text-xs">
                  <Plus className="h-3 w-3 mr-1" /> Feature
                </Button>
              </CollapsibleCard>
            ))}
            <Button variant="outline" onClick={() => addArrayItem("services", null, { id: `service-${Date.now()}`, icon: "Shield", title: "", shortDescription: "", features: [] })} className="text-gray-400 border-dashed border-white/10 text-sm mt-4 w-full py-6">
              <Plus className="h-4 w-4 mr-2" /> Add Service
            </Button>
          </SectionWrapper>
        </TabsContent>

        {/* AUDIENCES */}
        <TabsContent value="audiences">
          <SectionWrapper onSave={() => saveSection("audiences")} saving={saving}>
            {config.audiences?.map((aud, i) => (
              <CollapsibleCard key={i} title={aud.title || `Audience ${i + 1}`} onDelete={() => removeArrayItem("audiences", null, i)}>
                <FieldRow>
                  <Field label="Icon" value={aud.icon} onChange={(v) => updateField("audiences", `${i}.icon`, v)} />
                  <Field label="Title" value={aud.title} onChange={(v) => updateField("audiences", `${i}.title`, v)} />
                </FieldRow>
                <FieldTextarea label="Description" value={aud.description} onChange={(v) => updateField("audiences", `${i}.description`, v)} />
                <Label className="text-gray-500 text-xs block mt-3 mb-2">Scenarios</Label>
                {aud.scenarios?.map((sc, si) => (
                  <div key={si} className="bg-[#0A0A0F] rounded-lg p-4 mb-3 border border-white/[0.03]">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400 text-xs font-medium">Scenario {si + 1}</span>
                      <Button variant="ghost" size="sm" onClick={() => removeArrayItem("audiences", `${i}.scenarios`, si)} className="text-red-400 hover:bg-red-500/10 h-7 px-2">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <Field label="Title" value={sc.title} onChange={(v) => updateField("audiences", `${i}.scenarios.${si}.title`, v)} />
                    <FieldTextarea label="Example" value={sc.example} onChange={(v) => updateField("audiences", `${i}.scenarios.${si}.example`, v)} />
                    <FieldTextarea label="Outcome" value={sc.outcome} onChange={(v) => updateField("audiences", `${i}.scenarios.${si}.outcome`, v)} />
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addArrayItem("audiences", `${i}.scenarios`, { title: "", example: "", outcome: "" })} className="text-gray-400 border-dashed border-white/10 text-xs">
                  <Plus className="h-3 w-3 mr-1" /> Scenario
                </Button>
              </CollapsibleCard>
            ))}
            <Button variant="outline" onClick={() => addArrayItem("audiences", null, { id: `aud-${Date.now()}`, icon: "Users", title: "", description: "", scenarios: [] })} className="text-gray-400 border-dashed border-white/10 text-sm mt-4 w-full py-6">
              <Plus className="h-4 w-4 mr-2" /> Add Audience
            </Button>
          </SectionWrapper>
        </TabsContent>

        {/* HOW IT WORKS */}
        <TabsContent value="howItWorks">
          <SectionWrapper onSave={() => saveSection("howItWorks")} saving={saving}>
            {config.howItWorks?.map((step, i) => (
              <CollapsibleCard key={i} title={`${step.number} — ${step.title || "Step"}`} onDelete={() => removeArrayItem("howItWorks", null, i)}>
                <FieldRow>
                  <Field label="Number" value={step.number} onChange={(v) => updateField("howItWorks", `${i}.number`, v)} />
                  <Field label="Title" value={step.title} onChange={(v) => updateField("howItWorks", `${i}.title`, v)} />
                </FieldRow>
                <FieldTextarea label="Description" value={step.description} onChange={(v) => updateField("howItWorks", `${i}.description`, v)} />
              </CollapsibleCard>
            ))}
            <Button variant="outline" onClick={() => addArrayItem("howItWorks", null, { number: String(config.howItWorks?.length + 1 || 1).padStart(2, "0"), title: "", description: "" })} className="text-gray-400 border-dashed border-white/10 text-sm mt-4 w-full py-6">
              <Plus className="h-4 w-4 mr-2" /> Add Step
            </Button>
          </SectionWrapper>
        </TabsContent>

        {/* FAQ */}
        <TabsContent value="faq">
          <SectionWrapper onSave={() => saveSection("faq")} saving={saving}>
            {config.faq?.map((item, i) => (
              <CollapsibleCard key={i} title={item.question || `Question ${i + 1}`} onDelete={() => removeArrayItem("faq", null, i)}>
                <Field label="Question" value={item.question} onChange={(v) => updateField("faq", `${i}.question`, v)} />
                <FieldTextarea label="Answer" value={item.answer} onChange={(v) => updateField("faq", `${i}.answer`, v)} rows={4} />
              </CollapsibleCard>
            ))}
            <Button variant="outline" onClick={() => addArrayItem("faq", null, { question: "", answer: "" })} className="text-gray-400 border-dashed border-white/10 text-sm mt-4 w-full py-6">
              <Plus className="h-4 w-4 mr-2" /> Add FAQ Item
            </Button>
          </SectionWrapper>
        </TabsContent>

        {/* FOOTER */}
        <TabsContent value="footer">
          <SectionWrapper onSave={() => saveSection("footer")} saving={saving}>
            <FieldGroup title="Footer Content">
              <FieldTextarea 
                label="Footer Description" 
                value={config.footer?.description} 
                onChange={(v) => updateField("footer", "description", v)} 
                rows={3}
              />
            </FieldGroup>
            <FieldGroup title="Copyright & Compliance">
              <FieldRow>
                <Field 
                  label="Copyright Text" 
                  value={config.footer?.copyrightText} 
                  onChange={(v) => updateField("footer", "copyrightText", v)}
                  placeholder="All rights reserved."
                />
                <Field 
                  label="Compliance Text" 
                  value={config.footer?.complianceText} 
                  onChange={(v) => updateField("footer", "complianceText", v)}
                  placeholder="POPIA Compliant • Professional & Confidential"
                />
              </FieldRow>
            </FieldGroup>
          </SectionWrapper>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ========================
// Helper Components
// ========================

function SectionWrapper({ children, onSave, saving }) {
  return (
    <div className="space-y-6">
      {children}
      <div className="flex justify-end pt-4 border-t border-white/[0.04]">
        <Button onClick={onSave} disabled={saving} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-8 font-medium transition-colors">
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

function FieldGroup({ title, children }) {
  return (
    <div className="bg-[#111118] border border-white/[0.04] rounded-xl p-6">
      <h3 className="text-white font-semibold text-sm mb-4">{title}</h3>
      {children}
    </div>
  );
}

function FieldRow({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">{children}</div>;
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div className="space-y-1">
      <Label className="text-gray-500 text-xs">{label}</Label>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-[#0A0A0F] border-white/[0.06] text-white text-xs h-9"
      />
    </div>
  );
}

function FieldTextarea({ label, value, onChange, rows = 3 }) {
  return (
    <div className="space-y-1 mb-3">
      <Label className="text-gray-500 text-xs">{label}</Label>
      <Textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="bg-[#0A0A0F] border-white/[0.06] text-white text-xs resize-none"
      />
    </div>
  );
}

function CollapsibleCard({ title, onDelete, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#0D0D14] border border-white/[0.04] rounded-xl mb-3 overflow-hidden">
      <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/[0.02]" onClick={() => setOpen(!open)}>
        <span className="text-white text-sm font-medium truncate mr-4">{title}</span>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-red-400 hover:bg-red-500/10 h-7 px-2">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          {open ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
        </div>
      </div>
      {open && <div className="p-4 pt-0 border-t border-white/[0.03]">{children}</div>}
    </div>
  );
}

function ImageFieldWithUpload({ label, value, onChange, authAxios }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError("");
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await authAxios.post("/uploads", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Build the full URL for the uploaded file
      const uploadedUrl = `${BACKEND_URL}${res.data.url}`;
      onChange(uploadedUrl);
    } catch (err) {
      const detail = err.response?.data?.detail || "Upload failed";
      setUploadError(detail);
      setTimeout(() => setUploadError(""), 4000);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="mb-5">
      <Label className="text-gray-400 text-xs capitalize mb-1.5 block">{label}</Label>
      <div className="flex gap-3 items-start">
        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <Input
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              className="bg-[#0A0A0F] border-white/[0.06] text-white text-xs h-9 flex-1"
              placeholder="Enter image URL or upload a file"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileUpload}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="h-9 px-3 border-white/10 text-gray-400 hover:text-white hover:border-[#DC2626]/30 hover:bg-[#DC2626]/10 shrink-0 transition-colors"
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              <span className="ml-1.5 text-xs">{uploading ? "Uploading..." : "Upload"}</span>
            </Button>
          </div>
          {uploadError && (
            <p className="text-red-400 text-xs flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {uploadError}
            </p>
          )}
        </div>
        {value && (
          <img src={value} alt={label} className="h-16 w-24 object-cover rounded-lg border border-white/10 shrink-0" />
        )}
      </div>
    </div>
  );
}

